import React, { useState } from "react";
import { getCurrentCoordinates } from "../../../utils/geolocation";

export interface BranchCreateModalProps {
  onClose: () => void;
  onSubmit: (payload: BranchFormValues) => Promise<boolean>;
  isSubmitting?: boolean;
  titleText?: string;
  submitText?: string;
  initialValues?: Partial<BranchFormValues>;
  /**
   * Hiện ô "Kích hoạt chi nhánh" — chỉ dùng ở form CẬP NHẬT. Chi nhánh mới luôn được tạo ở trạng thái
   * hoạt động nên form tạo không cần hỏi.
   */
  showActiveToggle?: boolean;
}

export interface BranchFormValues {
  code: string;
  name: string;
  description: string;
  phone?: string;
  user_count?: number;
  address: string;
  isActive: boolean;
  /** Chi nhánh này là NHÀ MÁY giặt — hàng đi qua điểm trung chuyển bằng xe tải. */
  isFactory: boolean;
  companyId: number;
  /**
   * Tọa độ + bán kính hợp lệ để chấm công tại chi nhánh này (hrm). `null`/rỗng = chưa cấu hình riêng
   * — hrm sẽ fallback về tọa độ trụ sở chính (Cài đặt chung).
   */
  latitude: number | null;
  longitude: number | null;
  attendanceRadiusM: number | null;
}

export const BranchFormValuesDefault: BranchFormValues = {
  code: "",
  name: "",
  address: "",
  description: "",
  phone: "",
  user_count: 0,
  companyId: 0,
  isActive: true,
  isFactory: false,
  latitude: null,
  longitude: null,
  attendanceRadiusM: null,
};

const BranchCreateModal: React.FC<BranchCreateModalProps> = ({
  onClose,
  onSubmit,
  isSubmitting = false,
  titleText = "Tạo chi nhánh mới",
  submitText = "Tạo mới",
  initialValues = BranchFormValuesDefault,
  showActiveToggle = false,
}) => {
  const [form, setForm] = useState<BranchFormValues>({
    ...BranchFormValuesDefault,
    ...initialValues,
  });
  // Lỗi hiện ngay dưới ô nhập; xoá khi người dùng gõ lại để không "mắng" lúc họ đang sửa.
  const [errors, setErrors] = useState<{ name?: string; address?: string }>({});
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  const handleChange = (field: keyof BranchFormValues, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === "name" || field === "address") {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleUseCurrentLocation = async () => {
    setIsLocating(true);
    setLocationError(null);
    try {
      const { latitude, longitude } = await getCurrentCoordinates();
      setForm((prev) => ({ ...prev, latitude, longitude }));
    } catch (error: any) {
      setLocationError(error?.message ?? "Không thể lấy vị trí hiện tại.");
    } finally {
      setIsLocating(false);
    }
  };

  const handleClearCoordinates = () => {
    setLocationError(null);
    setForm((prev) => ({ ...prev, latitude: null, longitude: null, attendanceRadiusM: null }));
  };

  const hasCoordinates = form.latitude != null || form.longitude != null || form.attendanceRadiusM != null;

  const validate = (): boolean => {
    const next: { name?: string; address?: string } = {};
    if (!form.name.trim()) next.name = "Vui lòng nhập tên chi nhánh";
    if (!form.address.trim()) next.address = "Vui lòng nhập địa chỉ chi nhánh";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const success = await onSubmit(form);
    if (success) onClose();
  };

  const inputClass = (hasError?: boolean) =>
    `w-full rounded-lg border px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 ${
      hasError
        ? "border-red-400 focus:ring-red-400 focus:border-red-400"
        : "border-slate-300 focus:ring-green-500 focus:border-green-500"
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-4 md:px-0">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-xl border border-slate-200 animate-fadeIn">
        {/* Header — cố định, không cuộn theo nội dung */}
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 p-5">
          <h2 className="text-lg font-semibold text-slate-800">
            {titleText}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-green-600 hover:bg-green-50 rounded-full w-8 h-8 flex items-center justify-center transition"
          >
            ✕
          </button>
        </div>

        {/* Body — phần duy nhất cuộn được, tránh tràn khi nội dung form dài hơn màn hình */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Tên chi nhánh <span className="text-red-500">*</span>
            </label>
            <input
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="Nhập tên chi nhánh"
              className={inputClass(!!errors.name)}
            />
            {errors.name && (
              <p className="mt-1 text-xs text-red-500">{errors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Số điện thoại
            </label>
            <input
              value={form.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              placeholder="Nhập số điện thoại"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Mô tả
            </label>
            <textarea
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Nhập mô tả chi nhánh"
              rows={2}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Địa chỉ <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.address}
              onChange={(e) => handleChange("address", e.target.value)}
              placeholder="Nhập địa chỉ chi nhánh"
              rows={2}
              className={inputClass(!!errors.address)}
            />
            {errors.address && (
              <p className="mt-1 text-xs text-red-500">{errors.address}</p>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Tọa độ chấm công
                </label>
                <p className="text-xs text-slate-400">
                  Dùng để tính khoảng cách khi nhân viên chấm công tại chi nhánh này. Bỏ trống = dùng
                  tọa độ trụ sở chính (Cài đặt chung).
                </p>
              </div>
              <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={handleUseCurrentLocation}
                  disabled={isLocating}
                  className="rounded-lg border border-green-500 px-3 py-2 text-xs font-semibold text-green-600 hover:bg-green-50 disabled:opacity-60 whitespace-nowrap"
                >
                  {isLocating ? "Đang định vị..." : "📍 Lấy vị trí hiện tại"}
                </button>
                {hasCoordinates && (
                  <button
                    type="button"
                    onClick={handleClearCoordinates}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-50 whitespace-nowrap"
                  >
                    🗑 Xoá, dùng trụ sở chính
                  </button>
                )}
              </div>
            </div>

            {locationError && <p className="mt-2 text-xs text-red-500">{locationError}</p>}

            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Vĩ độ (latitude)</label>
                <input
                  type="number"
                  step="any"
                  value={form.latitude ?? ""}
                  onChange={(e) => handleChange("latitude", e.target.value === "" ? null : Number(e.target.value))}
                  placeholder="VD: 10.762622"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Kinh độ (longitude)</label>
                <input
                  type="number"
                  step="any"
                  value={form.longitude ?? ""}
                  onChange={(e) => handleChange("longitude", e.target.value === "" ? null : Number(e.target.value))}
                  placeholder="VD: 106.660172"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Bán kính hợp lệ (mét)</label>
                <input
                  type="number"
                  min={0}
                  step={10}
                  value={form.attendanceRadiusM ?? ""}
                  onChange={(e) => handleChange("attendanceRadiusM", e.target.value === "" ? null : Number(e.target.value))}
                  placeholder="VD: 200"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>
          </div>

          <div className="flex items-start gap-2 mt-2">
            <input
              id="isFactory"
              type="checkbox"
              checked={form.isFactory}
              onChange={(e) => handleChange("isFactory", e.target.checked)}
              className="h-4 w-4 mt-0.5 rounded border-slate-300 text-green-600 focus:ring-green-500"
            />
            <label htmlFor="isFactory" className="text-sm text-slate-700">
              Là nhà máy giặt
              <span className="block text-xs text-slate-400">
                Hàng dơ/sạch của nhà máy đi qua điểm trung chuyển bằng xe tải — bật tính năng bàn giao xe
                trung chuyển cho đơn liên quan.
              </span>
            </label>
          </div>

          {/* Chỉ form CẬP NHẬT mới hỏi trạng thái — chi nhánh mới luôn tạo ở trạng thái hoạt động. */}
          {showActiveToggle && (
            <div className="flex items-center gap-2 mt-2">
              <input
                id="isActive"
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => handleChange("isActive", e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-green-600 focus:ring-green-500"
              />
              <label htmlFor="isActive" className="text-sm text-slate-700">
                Kích hoạt chi nhánh
              </label>
            </div>
          )}
        </div>

        {/* Footer — cố định, không cuộn theo nội dung */}
        <div className="shrink-0 border-t border-slate-200 p-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200 transition"
          >
            Hủy
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-5 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold shadow-sm hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition disabled:opacity-70"
          >
            {isSubmitting ? "Đang lưu..." : submitText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BranchCreateModal;
